import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { AppState } from '../../store/interfaces';
import { Carousel } from '../../common/Carousel';
import { getSettings } from '../../store/settings/actions';
import urls from '../../config/urls';
import { CloseButton } from '../../common/CloseButton';
import { setWizardStep } from '../../store/app/acions';
import { OverlayPage } from '../../common/OverlayPage';

import { Intro } from './steps/Intro';
import { BeginningOfWork } from './steps/BeginningOfWork';
import { WorkingTime } from './steps/WorkingTime';
import { WorkingDays } from './steps/WorkingDays';
import { WizardSteps } from './interfaces';
import { useSetNextWizardStep } from './hooks';
import { MinimumRidePayout } from './steps/MinimumRidePayout';
import { MaxMilesToPickUp } from './steps/MaxMilesToPickUp';
import { PickUpOutsideState } from './steps/PickUpOutsideState';
import { DropOffOutsideState } from './steps/DropOffOutsideState';
import { AllDone } from './steps/AllDone';
import { Markers } from './steps/Markers';
import { usePermissionPages } from './permissions/usePermissionPages';
import PermissionPage from './permissions/PermissionPage';

const wizardSteps: WizardSteps = new Map([
  ['intro', Intro],
  ['markers', Markers],
  ['beginningOfWork', BeginningOfWork],
  ['workingTime', WorkingTime],
  ['workingDays', WorkingDays],
  ['minimumRidePayout', MinimumRidePayout],
  ['maxMilesToPickUp', MaxMilesToPickUp],
  /*
  ['pickUpOutsideState', PickUpOutsideState],
  ['dropOffOutsideState', DropOffOutsideState],
   */
  ['allDone', AllDone],
]);

const Wizard = React.memo(function () {
  const wizardStep = useSelector((app: AppState) => app.app.wizardStep);
  const setNextWizardStep = useSetNextWizardStep(wizardSteps);
  const dispatch = useDispatch();
  const hasActiveMarker = useSelector((app: AppState) => app.app.hasActiveMarker);

  useEffect(() => {
    if (!wizardStep) {
      return;
    }

    dispatch(getSettings.request());
  }, [!!wizardStep]);

  const location = useLocation();
  const StepComponent = wizardSteps.get(wizardStep);

  if (StepComponent) {
    return (
      <Carousel>
        {location.pathname === urls.settings && (
          <CloseButton onClick={() => dispatch(setWizardStep(null))} />
        )}
        <StepComponent setNextWizardStep={setNextWizardStep} />
      </Carousel>
    );
  }

  if (hasActiveMarker === false) {
    return (
      <OverlayPage zIndex={3000}>
        <Markers showContinueButton={false} setNextWizardStep={() => {}} />
      </OverlayPage>
    );
  }

  return null;
});

export default function () {
  const { showLocationPage, showBatteryPage, showNotificationPage, showGpsPage } =
    usePermissionPages();
  const user = useSelector((state: AppState) => state.auth.user, shallowEqual);

  if (!user) {
    return null;
  }

  if (showLocationPage) {
    return (
      <PermissionPage
        permission="location"
        withPrivacy
        zendeskLink="https://skeddy.zendesk.com/hc/en-us/articles/24987920962199"
      />
    );
  }

  if (showBatteryPage) {
    return (
      <PermissionPage
        permission="battery"
        zendeskLink="https://skeddy.zendesk.com/hc/en-us/articles/20552666852503"
      />
    );
  }

  if (showNotificationPage) {
    return (
      <PermissionPage
        permission="notification"
        zendeskLink="https://skeddy.zendesk.com/hc/en-us/articles/25287307560855"
      />
    );
  }

  if (showGpsPage) {
    return (
      <PermissionPage
        permission="gps"
        zendeskLink="https://skeddy.zendesk.com/hc/en-us/articles/28456301050903"
      />
    );
  }

  return <Wizard />;
}
